/** GPX import — the other half of gpx.ts's escape hatch: turn a GPX file
 * from anywhere (a phone-recorded track, someone else's planned route, a
 * random trailhead site's download) into an editable Plan. Pure and
 * node-safe: vitest runs in node, so this is a tolerant regex scanner over
 * the raw text rather than DOMParser — matches trkpt/rtept/wpt loosely
 * enough to survive attribute-order and quoting differences across GPX
 * producers, the same real-world tolerance the phone's GPXImporter (an
 * XMLParser, which gets this for free) has.
 *
 * Never throws: a hopeless or truncated file just returns null, same as
 * "fewer than 2 points parsed" — the caller decides what to tell the user,
 * not this function. */

import type { LonLat, Plan, PlanLeg, Waypoint } from "./types";

/** Above this many points, editing every one as a draggable control point
 * stops being useful — a 10 Hz recorded track can carry tens of thousands.
 * Stride-sample down to this many, keeping both endpoints. */
const MAX_POINTS = 400;

/** Matches one whole `<wpt ...>` element, self-closing or with a body.
 * Reused both to pull waypoints out and to strip them before hunting for
 * the plan's name — a wpt's own `<name>` doesn't count as that. Returned
 * fresh each call: a regex literal evaluates to a new RegExp per
 * evaluation, so the two call sites never fight over shared lastIndex
 * state. */
function wptBlockPattern(): RegExp {
  return /<wpt\b[^>]*?(?:\/>|>[\s\S]*?<\/wpt>)/gi;
}

/** Tolerant attribute read: order-independent, single- or double-quoted.
 * Anchored on a preceding boundary so reading "lat" can't match inside
 * some other, longer attribute name that merely contains it. */
function extractAttr(tag: string, name: string): number | null {
  const re = new RegExp(`(?:^|\\s)${name}\\s*=\\s*("([^"]*)"|'([^']*)')`, "i");
  const m = re.exec(tag);
  if (!m) return null;
  const n = Number(m[2] ?? m[3] ?? "");
  return Number.isFinite(n) ? n : null;
}

/** Decodes exactly the five XML entities, &amp; last — so a name whose
 * source text is itself doubly-escaped (e.g. a literal "&apos;" that got
 * escaped to "&amp;apos;" on export) decodes in one pass, the way a real
 * XML parser reads it, rather than a second pass turning it into an
 * apostrophe that was never there. Mirrors gpx.ts's escapeXML doing & '
 * first for the same reason in reverse. */
function decodeXMLEntities(s: string): string {
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
}

function stripExtension(filename: string): string {
  const i = filename.lastIndexOf(".");
  return i > 0 ? filename.slice(0, i) : filename;
}

/** trkpt and rtept read interchangeably, in document order — the phone's
 * GPXImporter does the same; it has no separate path for routes vs.
 * tracks. */
function extractPoints(xml: string): LonLat[] {
  const points: LonLat[] = [];
  for (const m of xml.matchAll(/<(?:trkpt|rtept)\b[^>]*>/gi)) {
    const lat = extractAttr(m[0], "lat");
    const lon = extractAttr(m[0], "lon");
    if (lat !== null && lon !== null) points.push([lon, lat]);
  }
  return points;
}

function extractWaypoints(xml: string): Waypoint[] {
  const waypoints: Waypoint[] = [];
  for (const block of xml.match(wptBlockPattern()) ?? []) {
    const lat = extractAttr(block, "lat");
    const lon = extractAttr(block, "lon");
    if (lat === null || lon === null) continue;
    const nameMatch = /<name>([\s\S]*?)<\/name>/i.exec(block);
    const name = (nameMatch && decodeXMLEntities(nameMatch[1]).trim()) || "Waypoint";
    waypoints.push({ at: [lon, lat], name });
  }
  return waypoints;
}

/** First `<name>` in the document that isn't inside a `<wpt>` — covers
 * `<trk><name>`, `<rte><name>`, and a top-level `<name>`, whichever comes
 * first, without needing real tag-nesting awareness to tell those three
 * apart. */
function extractName(xml: string): string {
  const withoutWaypoints = xml.replace(wptBlockPattern(), "");
  const m = /<name>([\s\S]*?)<\/name>/i.exec(withoutWaypoints);
  return m ? decodeXMLEntities(m[1]).trim() : "";
}

/** Stride-sample down to MAX_POINTS, always keeping points[0] and the
 * last point exactly. */
function decimate(points: LonLat[]): LonLat[] {
  if (points.length <= MAX_POINTS) return points;
  const stride = (points.length - 1) / (MAX_POINTS - 1);
  const out: LonLat[] = [];
  for (let i = 0; i < MAX_POINTS; i++) out.push(points[Math.round(i * stride)]);
  return out;
}

export function parseGPX(
  xml: string,
  filename: string,
  region: string,
  now: string,
): Plan | null {
  try {
    const parsed = extractPoints(xml);
    if (parsed.length < 2) return null;

    const points = decimate(parsed);
    const legs: PlanLeg[] = [];
    for (let i = 1; i < points.length; i++) legs.push({ mode: "straight", via: [] });

    return {
      name: extractName(xml) || stripExtension(filename),
      region,
      points,
      legs,
      waypoints: extractWaypoints(xml),
      createdAt: now,
      updatedAt: now,
    };
  } catch {
    return null;
  }
}
